import { logger } from '@/utils/log/logger';
import gqlClient, { cache } from '../gql';
import {
  contentsArticleSiteMapVariables,
  contentsAuthorVariables,
  contentsLoadMoreVariables,
  contentsVariables,
  GET_CONTENTS_SOFT_POPULATE,
  GET_FULL_CONTENTS,
  GET_SITE_MAP_ARTICLECONTENTS,
  CHECK_CONTENT_EXISTS,
  GET_TV_SHOW_DATA,
  GET_FIRST_AND_LAST_CONTENT,
} from '../graphql';
import { Content, ContentResponse } from '../model';

export const getContent = async (
  slug: string,
  languageCode?: string,
  draft?: boolean,
  type?: 'article' | 'video' | 'audio',
): Promise<Content | undefined> => {
  try {
    const variables = contentsVariables(
      slug,
      languageCode,
      undefined,
      draft,
      undefined,
      type,
    );

    const key = JSON.stringify(variables);
    if (cache.has(key)) {
      return cache.get(key) as Content;
    }

    const contentResponse = await gqlClient.query<ContentResponse>({
      fetchPolicy: 'no-cache',
      errorPolicy: 'all',
      query: GET_FULL_CONTENTS,
      variables,
    });

    if (contentResponse.data.Contents.docs[0]) {
      cache.set(key, contentResponse.data.Contents.docs[0]);
    }
    return contentResponse.data.Contents.docs[0];
  } catch (error) {
    logger.error(`Error getting content: ${error}`);
  }
};

export const getPreviewContent = async (
  slug: string,
  languageCode?: string,
): Promise<Content | undefined> => {
  try {
    const contentResponse = await fetch(
      `${process.env.NEXT_PUBLIC_REST_URL}/live-preview/content/${slug}?locale=${languageCode}`,
      {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${process.env.REST_API_SECRET_TOKEN}`,
        },
      },
    );
    const content = await contentResponse.json();
    return content.result.docs;
  } catch (error) {
    logger.error(`Error getting content: ${error}`);
  }
};

export const getContents = async ({
  where = {},
  locale,
  limit,
  sort,
}: {
  where: any;
  locale?: string;
  limit?: number;
  sort?: string;
}): Promise<Content[] | undefined> => {
  try {
    const contentResponse = await gqlClient.query<ContentResponse>({
      fetchPolicy: 'no-cache',
      errorPolicy: 'all',
      query: GET_FULL_CONTENTS,
      variables: { where, limit, locale, sort },
    });

    return contentResponse?.data?.Contents.docs;
  } catch (error) {
    logger.error(`Error getting contents: ${error}`);
  }
};

export const getSoftPopulateContents = async ({
  where = {},
  locale,
  limit,
}: {
  where: any;
  locale?: string;
  limit?: number;
}): Promise<Content[] | undefined> => {
  try {
    const contentResponse = await gqlClient.query<ContentResponse>({
      fetchPolicy: 'no-cache',
      errorPolicy: 'all',
      query: GET_CONTENTS_SOFT_POPULATE,
      variables: { where, limit, locale },
    });

    return contentResponse?.data?.Contents?.docs;
  } catch (error) {
    logger.error(`Error getting soft populate contents: ${error}`);
  }
};

export const checkContentExists = async ({
  where = {},
  locale,
  limit,
}: {
  where: any;
  locale?: string;
  limit?: number;
}): Promise<Content[] | undefined> => {
  try {
    const contentResponse = await gqlClient.query<ContentResponse>({
      fetchPolicy: 'no-cache',
      errorPolicy: 'all',
      query: CHECK_CONTENT_EXISTS,
      variables: { where, limit, locale },
    });

    return contentResponse?.data?.Contents.docs;
  } catch (error) {
    logger.error(`Error checking content exists: ${error}`);
  }
};

export const getLoadMoreContents = async (filters: {
  existingIds: string[];
  languageCode: string;
  channelId: string;
  page: number;
  primaryTopicId?: string;
  type?: 'opinion' | 'feature';
  limit?: number;
}): Promise<ContentResponse | undefined> => {
  try {
    const contentResponse = await gqlClient.query<ContentResponse>({
      fetchPolicy: 'no-cache',
      errorPolicy: 'all',
      query: GET_FULL_CONTENTS,
      variables: contentsLoadMoreVariables(filters),
    });

    return contentResponse?.data;
  } catch (error) {
    logger.error(`Error loading more contents: ${error}`);
  }
};

export const getSiteMapArticleContents = async (
  where: any = {},
  languageCode?: string,
  page?: number,
  limit?: number,
  type?: 'article' | 'video' | 'audio',
): Promise<ContentResponse['Contents'] | undefined> => {
  try {
    const contentResponse = await gqlClient.query<ContentResponse>({
      fetchPolicy: 'no-cache',
      errorPolicy: 'all',
      query: GET_SITE_MAP_ARTICLECONTENTS,
      variables: contentsArticleSiteMapVariables({
        where,
        languageCode: languageCode as string,
        page,
        draft: false,
        limit,
        type,
      }),
    });

    return contentResponse?.data?.Contents;
  } catch (error) {
    logger.error(`Error getting sitemap article contents: ${error}`);
  }
};

export const getFirstAndLastContent = async (
  where: any = {},
  languageCode?: string,
  page?: number,
  limit?: number,
  type?: 'article' | 'video' | 'audio',
): Promise<ContentResponse['Contents'] | undefined> => {
  try {
    const contentResponse = await gqlClient.query<ContentResponse>({
      fetchPolicy: 'no-cache',
      errorPolicy: 'all',
      query: GET_FIRST_AND_LAST_CONTENT,
      variables: contentsArticleSiteMapVariables({
        where,
        languageCode: languageCode as string,
        page,
        limit,
        type,
      }),
    });

    return contentResponse?.data?.Contents;
  } catch (error) {
    logger.error(`Error getting sitemap article contents: ${error}`);
  }
};

export const getAuthorContentsPageable = async (
  author: any = '',
  languageCode?: string,
  page?: number,
): Promise<ContentResponse | undefined> => {
  try {
    const contentResponse = await gqlClient.query<ContentResponse>({
      fetchPolicy: 'no-cache',
      errorPolicy: 'all',
      query: GET_FULL_CONTENTS,
      variables: contentsAuthorVariables(author, languageCode, page),
    });

    return contentResponse?.data;
  } catch (error) {
    logger.error(`Error getting author contents: ${error}`);
  }
};

export const getTVShowData = async ({
  where,
  limit,
  locale,
}: {
  where: any;
  limit: number;
  locale: string;
}): Promise<Content[] | undefined> => {
  try {
    const contentResponse = await gqlClient.query<ContentResponse>({
      fetchPolicy: 'no-cache',
      errorPolicy: 'all',
      query: GET_TV_SHOW_DATA,
      variables: { where, limit, locale },
    });

    return contentResponse?.data?.Contents.docs;
  } catch (error) {
    logger.error(`Error getting TV show data: ${error}`);
  }
};
